// Discovery client. First call the recipient web makes is
// `GET /share/discover/{token}` against share-bundle-service to
// determine whether this is an Option A (link-share PDF) or
// Option B (WebRTC tap-to-share) token.

export type ShareKind = "link" | "webrtc";

export interface DiscoverResponse {
  kind: ShareKind;
  /** webrtc shares only — the session's data classes, so the UI can dispatch
   * presence shares by purpose (longitudinal_record → dashboard). */
  data_classes?: string[];
  expires_at: string;
  // Present for link-share only — the AEAD-wrapped ciphertext byte length.
  byte_length?: number;
  // Link-share only. When true, the body served at the link download URL
  // is an opaque ChaCha20-Poly1305 ciphertext (not a PDF) that must be
  // decrypted client-side with the CEK carried in the URL fragment
  // (`#k=<base64url>`). When false/absent, the body is a plaintext PDF.
  // This is the zero-custody anyone-share.
  encrypted?: boolean;
}

/**
 * Hits the share-bundle-service discovery endpoint and returns the
 * token kind plus minimal metadata. Throws on 404 (not found),
 * 410 (revoked or expired), or transport errors — caller renders
 * appropriate UI.
 */
export async function discover(
  apiBaseUrl: string,
  token: string,
): Promise<DiscoverResponse> {
  const url = `${apiBaseUrl}/share/discover/${encodeURIComponent(token)}`;
  const resp = await fetch(url, {
    method: "GET",
    credentials: "omit",
    cache: "no-store",
  });
  if (resp.status === 404) {
    throw new DiscoverError("not_found", "This share link doesn't exist.");
  }
  if (resp.status === 410) {
    // A view-once link whose single view was already claimed returns
    // {"error":"consumed"} — distinct from a revoked/expired link so the
    // recipient sees an accurate "already viewed" message.
    const body = (await resp.json().catch(() => ({}))) as { error?: string };
    if (body.error === "consumed") {
      throw new DiscoverError(
        "consumed",
        "This emergency card link has already been viewed and is no longer available.",
      );
    }
    throw new DiscoverError(
      "gone",
      "This share link has been revoked or expired.",
    );
  }
  if (!resp.ok) {
    throw new DiscoverError(
      "unknown",
      `Unexpected response: ${resp.status} ${resp.statusText}`,
    );
  }
  const data = (await resp.json()) as DiscoverResponse;
  if (data.kind !== "link" && data.kind !== "webrtc") {
    throw new DiscoverError(
      "unknown",
      `Unexpected token kind: ${String(data.kind)}`,
    );
  }
  return data;
}

export type DiscoverErrorCode = "not_found" | "gone" | "consumed" | "unknown";

export class DiscoverError extends Error {
  constructor(
    public readonly code: DiscoverErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "DiscoverError";
  }
}
